import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLinkIcon } from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { Panel } from './ui/Panel';
import { ContentSections, type ContentBlock } from './ui/ContentSections';
import { pageSections, type EditablePage } from '../data/pageSections';
import { PageTextSearch, type SearchHit } from './PageTextSearch';
import type { Json } from './ui/ContentEditor';
import { btnGhost, inputCls } from './ui/styles';
import { useAdmin } from './AdminContext';
import { tokenHelp } from './adminLabels';
import { shopHref } from '../utils/hosts';
import { SeoPanel } from './ui/SeoPanel';
import { isSeoPageKey, pageSeoAuto } from '../utils/seoAuto';
import type { PageContent } from '../core/types/pages';
const sections: {
  key: Exclude<keyof PageContent, 'seo'>;
  label: string;
  path: string;
  description: string;
}[] = [{
  key: 'home',
  label: 'Home',
  path: '/',
  description: 'De homepage is één verhaal: Kijken, Kiezen, Opbouwen, en tot slot Kom langs. De volgorde ligt vast; jij bepaalt de woorden en beelden. De producten en protocollen kies je met “uitgelicht” in Aanbod.'
}, {
  key: 'shop',
  label: 'Webshop',
  path: '/webshop',
  description: 'De kop boven de collectie en het blok over protocollen. In de kop ook {count} (aantal producten) en {brands} (de merken).'
}, {
  key: 'routines',
  label: 'Protocollen',
  path: '/routines',
  description: 'De blokken in de volgorde van de pagina. De hoofdknop “Begin met je protocol” ligt vast; de protocollen zelf staan in Aanbod.'
}, {
  key: 'treatments',
  label: 'Salon',
  path: '/behandelingen',
  description: 'De blokken in de volgorde van de pagina. Elke boekknop heet “Plan een afspraak”; de behandeling kies je in de lade.'
}, {
  key: 'journal',
  label: 'Journal',
  path: '/journal',
  description: 'Koppen en de podcast. De artikelen zelf staan in Aanbod › Journal.'
}, {
  key: 'about',
  label: 'Over Sarah',
  path: '/over-ons',
  description: 'Je verhaal, filosofie, je plank en het slot “kom kennismaken”. De hoofdknop “Bekijk de collectie” ligt vast.'
}, {
  key: 'contact',
  label: 'Contact',
  path: '/contact',
  description: 'Kop, de soorten vragen met hun knop, en het blok over langskomen. Adres en route staan in Instellingen › Salon & contact, de dagen in › Salondagen.'
}, {
  key: 'portal',
  label: 'Mijn omgeving',
  path: '/account',
  description: 'Tips van de week, seizoensmeldingen en de redenen bij stoppen met het abonnement. Extra slimme woorden per tip: {product}, {week}, {weeks}, {treatment}, {courseSpacing}.'
}, {
  key: 'testimonials',
  label: 'Klantcitaten',
  path: '/',
  description: 'De citaten op de homepage. De eerste staat groot.'
}, {
  key: 'subscription',
  label: 'Bewust Abonnement',
  path: '/webshop',
  description: 'Uitleg en voordelen van het abonnement.'
}];

/** De blokken van een pagina in de volgorde van de site, met rol, toon en een link naar dat blok. */
const blocksFor = (key: EditablePage, path: string): ContentBlock[] | undefined => pageSections[key]?.map(s => ({
  key: s.key,
  title: s.title,
  role: s.role,
  tone: s.tone,
  labels: s.labels,
  href: shopHref(s.anchor ? `${path}#${s.anchor}` : path)
}));

/** Alle vaste teksten per pagina, met uitleg over de slimme woorden. */
export function PageTexts() {
  const {
    pages,
    setPages,
    changed
  } = useAdmin();
  const [params, setParams] = useSearchParams();
  const [tokensOpen, setTokensOpen] = useState(false);
  const active = sections.find(s => s.key === params.get('pagina')) ?? sections[0];
  const [focus, setFocus] = useState<(SearchHit & {
    n: number;
  }) | null>(null);
  const searchable = useMemo(() => sections.map(s => ({
    key: s.key,
    label: s.label,
    value: pages[s.key] as unknown as Json
  })), [pages]);
  const pick = (hit: SearchHit) => {
    setParams({
      pagina: hit.page
    }, {
      replace: true
    });
    // Teller erbij: twee keer hetzelfde blok kiezen opent en scrollt opnieuw.
    setFocus(f => ({
      ...hit,
      n: (f?.n ?? 0) + 1
    }));
  };
  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast(`${token} gekopieerd`, {
        description: 'Plak het in een tekstveld.'
      });
    } catch {
      toast(token);
    }
  };
  return <div data-id="element-3585" data-mp="3aeuftmdojgi3">
      <PageHeader title="Paginateksten" description="Alle vaste teksten op de website. De opbouw van de pagina blijft gelijk; jij bepaalt de woorden." data-id="element-3586" data-mp="3aii7sh3h7uup" />

      <div className="mb-6 max-w-xl" data-id="element-3587" data-mp="3kfehur90wp1d">
        <PageTextSearch pages={searchable} onPick={pick} data-id="element-3588" data-mp="3kecwntyamm5x" />
      </div>

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-3589" data-mp="1zkhmpcwuvfl">
        <nav aria-label="Pagina’s" className="lg:col-span-3" data-id="element-3590" data-mp="3t6wxn12252tx">
          {/* Telefoon: de eigen keuzelijst van het toestel. Negen pagina's passen niet naast elkaar,
              en een scrollrij verstopt de helft; de native lijst toont alles en is groot genoeg om te tikken. */}
          <label className="block lg:hidden" data-id="element-3591" data-mp="6xlxl1f1iemu">
            <span className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3592" data-mp="ql8ng34ggs5t">Pagina</span>
            <select value={active.key} onChange={e => setParams({
            pagina: e.target.value
          }, {
            replace: true
          })} className={inputCls} data-id="element-3593" data-mp="unlsepbdom1j">
              {sections.map(s => <option key={s.key} value={s.key} data-id="element-3594" data-mp="ll4485c5xjbw">
                  {s.label}
                </option>)}
            </select>
          </label>
          <ul className="hidden flex-col gap-1 lg:flex" data-id="element-3595" data-mp="bnzg3zyrbdsg">
            {sections.map(s => <li key={s.key} data-id="element-3596" data-mp="3u27dquyhpcsq">
                <button type="button" onClick={() => setParams({
              pagina: s.key
            }, {
              replace: true
            })} aria-current={active.key === s.key ? 'page' : undefined} className={`w-full rounded-md px-3 py-2 text-left text-[14px] transition-colors duration-150 ease-soft ${active.key === s.key ? 'bg-white text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:bg-white/60 hover:text-ink'}`} data-id="element-3597" data-mp="3u30cb8naovzs">
                  {s.label}
                </button>
              </li>)}
          </ul>

          <div className="mt-6 hidden rounded-lg border border-line bg-white p-4 lg:block" data-id="element-3598" data-mp="2w9hhxgaha25p">
            <p className="text-[13px] font-medium text-ink" data-id="element-3599" data-mp="2rao4fnv6yb5j">Slimme woorden</p>
            <p className="mt-1 text-[12px] leading-relaxed text-muted" data-id="element-3600" data-mp="3oxwpxiv6zk7j">Deze vult de site zelf in. Verandert je telefoonnummer, dan klopt elke tekst meteen.</p>
            <ul className="mt-3 space-y-1.5" data-id="element-3601" data-mp="3jw7iseb3bqoq">
              {tokenHelp.map(t => <li key={t.token} data-id="element-3602" data-mp="3excp2hbl4hpu">
                  <button type="button" onClick={() => void copyToken(t.token)} aria-label={`${t.token} kopiëren: ${t.meaning}`} title="Klik om te kopiëren" className="group -mx-1.5 block w-full rounded-md px-1.5 py-1 text-left text-[12px] transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-3603" data-mp="3a0crkusswnac">
                    <code className="rounded bg-sand px-1.5 py-0.5 text-ink group-hover:bg-blush" data-id="element-3604" data-mp="j1849cs0rmry">{t.token}</code>{' '}
                    <span className="text-muted" data-id="element-3605" data-mp="o0aye26go8l5">{t.meaning}</span>
                  </button>
                </li>)}
            </ul>
          </div>
        </nav>

        <div className="lg:col-span-9" data-id="element-3606" data-mp="xrfdd1w4ztfz">
          <Panel title={active.label} description={active.description} actions={<a href={shopHref(active.path)} target="_blank" rel="noopener noreferrer" className={`${btnGhost} shrink-0`} data-id="element-3608" data-mp="11uc094spmbpq">
                Bekijk pagina
                <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-3609" data-mp="1bpm3fvuugp53" />
                <span className="sr-only" data-id="element-3610" data-mp="38jyydywuda4r"> (opent in een nieuw tabblad)</span>
              </a>} data-id="element-3607" data-mp="xqcpz3y0w94t">
            <button type="button" onClick={() => setTokensOpen(o => !o)} aria-expanded={tokensOpen} aria-controls="token-help" className="mb-5 text-[12px] text-clay underline underline-offset-2 lg:hidden" data-id="element-3611" data-mp="3dijyzzg09vtq">
              {tokensOpen ? 'Verberg slimme woorden' : 'Wat betekent {firstName}?'}
            </button>
            {tokensOpen && <ul id="token-help" className="mb-5 grid gap-1 rounded-md bg-shell p-3 text-[12px] lg:hidden" data-id="element-3612" data-mp="1o390147uzonq">
                {tokenHelp.map(t => <li key={t.token} data-id="element-3613" data-mp="vdah7qk9cnri">
                    <code className="text-ink" data-id="element-3614" data-mp="vebvs50hfsdz">{t.token}</code> <span className="text-muted" data-id="element-3615" data-mp="1aa1adi3c938o">{t.meaning}</span>
                  </li>)}
              </ul>}
            <ContentSections key={active.key} value={pages[active.key] as unknown as Json} onChange={next => setPages(p => ({
            ...p,
            [active.key]: next
          }))} focusKey={focus?.page === active.key ? focus.block : null} focusTick={focus?.n} blocks={blocksFor(active.key, active.path)} data-id="element-3616" data-mp="158u2lj174420" />
          </Panel>
          {isSeoPageKey(active.key) && <div className="mt-6" data-id="element-3617" data-mp="2f8occpoky48m">
              <SeoPanel key={active.key} seo={pages.seo?.[active.key]} onChange={seo => {
            const key = active.key;
            if (!isSeoPageKey(key)) return;
            setPages(p => ({
              ...p,
              seo: {
                ...p.seo,
                [key]: seo
              }
            }));
          }} auto={pageSeoAuto(active.key)} path={active.path} imageFallback="de algemene deelfoto van de site" data-id="element-3618" data-mp="2f9g6dw2j3tws" />
            </div>}
          {changed.pages && <p className="mt-3 text-[12px] text-muted" data-id="element-3619" data-mp="326rjp07ckwir">Vergeet niet rechtsboven op Opslaan te klikken.</p>}
        </div>
      </div>
    </div>;
}